import React, { useMemo } from 'react';

import { Box, Button, Container, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import canAccessProcessingActivitiesAdmin from 'config/permissions/canAccessProcessingActivitiesAdmin';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import isProcessingActivityDraftInProgress from '../_shared/isProcessingActivityDraftInProgress';

import ProcessingActivityAssetProcessingsSection from './_components/ProcessingActivityAssetProcessingsSection';
import ProcessingActivityChangelogSection from './_components/ProcessingActivityChangelogSection';
import ProcessingActivityDetailMetricsSection from './_components/ProcessingActivityDetailMetricsSection';
import ProcessingActivityPublishedSection from './_components/ProcessingActivityPublishedSection';
import ProcessingActivityPurposesReadSection from './_components/ProcessingActivityPurposesReadSection';
import useStyles from './ProcessingActivityDetailPage.styles';
import useProcessingActivityDetail from './useProcessingActivityDetail';

const getMessageFromMap = (map: Record<string, string[]>, code: string, t: (message: string | string[]) => string): string => {
  const message = map[code];
  return message ? t(message) : code;
};

const ProcessingActivityDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const match = useRouteMatch<{ id: string }>(PATHS.PROCESSING_ACTIVITIES_DETAIL);
  const processingActivityId = match ? Number(match.params.id) : undefined;

  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canManageProcessingActivities = canAccessProcessingActivitiesAdmin(userData);

  const { detail, isError, isForbidden, isLoading, metrics, refetch, viewModel } = useProcessingActivityDetail({
    organizationId,
    processingActivityId,
  });

  const draftInProgress = detail ? isProcessingActivityDraftInProgress(detail) : false;

  const pageSubtitle = useMemo(() => {
    if (!detail) return '';

    const roleLabel = detail.processingRole
      ? getMessageFromMap(processing_activities_messages.processing_role, detail.processingRole, t)
      : t(processing_activities_messages.review_placeholder);
    const versionLabel =
      detail.publishedVersionNumber != null
        ? t(processing_activities_messages.table.version_label, { number: detail.publishedVersionNumber })
        : t(processing_activities_messages.review_placeholder);
    const statusLabel = detail.status
      ? getMessageFromMap(processing_activities_messages.status, detail.status, t)
      : t(processing_activities_messages.status.draft);

    return t(processing_activities_messages.detail.subtitle, {
      role: roleLabel,
      status: statusLabel,
      version: versionLabel,
    });
  }, [detail, t]);

  const openEditPage = () => {
    if (!processingActivityId) return;
    history.push(PATHS.PROCESSING_ACTIVITIES_EDIT.replace(':id', String(processingActivityId)));
  };

  if (!canManageProcessingActivities) {
    return (
      <Container>
        <Box className={styles.forbiddenState}>
          <Typography gutterBottom variant='h6'>
            {t(processing_activities_messages.forbidden_title)}
          </Typography>
          <Typography color='textSecondary' variant='body2'>
            {t(processing_activities_messages.forbidden_body)}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <Loader inner />
      </Container>
    );
  }

  if (isForbidden || isError || !detail || !viewModel) {
    return (
      <Container>
        <Box className={styles.errorBanner}>
          <Typography color='error' gutterBottom variant='body2'>
            {t(processing_activities_messages.detail.errors.load_failed)}
          </Typography>
          {!isForbidden && <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>}
          <Box marginTop={2}>
            <Button color='primary' onClick={() => history.push(PATHS.PROCESSING_ACTIVITIES)} variant='outlined'>
              {t(processing_activities_messages.detail.actions.back)}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          backButton
          bottomAdornment={
            draftInProgress ? (
              <div className={styles.draftNotice} role='status'>
                <span aria-hidden='true' className={clsx(styles.statusDot, styles.statusDotWarning)} />
                {t(processing_activities_messages.detail.draft_indicator)}
              </div>
            ) : undefined
          }
          noBorder
          onBack={() => history.push(PATHS.PROCESSING_ACTIVITIES)}
          rightAdornment={
            <ColoredButton
              className=''
              customColor='primary'
              disabled={false}
              icon={false}
              onClick={openEditPage}
              showLoader={false}
              textLike={false}
              variant='contained'
            >
              {draftInProgress
                ? t(processing_activities_messages.detail.actions.open_draft)
                : t(processing_activities_messages.detail.actions.edit)}
            </ColoredButton>
          }
          subtitle={pageSubtitle}
        >
          {detail.name}
        </ManagePageHeader>

        <ProcessingActivityDetailMetricsSection isLoading={false} metrics={metrics} />

        <div className={styles.detailGrid}>
          <ProcessingActivityPublishedSection detail={detail} viewModel={viewModel} />
          <ProcessingActivityChangelogSection entries={viewModel.changelog} />
        </div>

        <ProcessingActivityPurposesReadSection purposes={viewModel.purposes} />

        <ProcessingActivityAssetProcessingsSection organizationId={organizationId} processingActivityId={processingActivityId} />
      </Container>
    </ErrorBoundary>
  );
};

export default ProcessingActivityDetailPage;
